<template>
  <div>
    <zb-modal ref="detailModal" class="slide-in-modal" @close="handleModalClose">
      <h2 slot="title" class="mb-6" v-if="activeProgramme">
        {{ activeProgramme.name }}
      </h2>

      <template v-slot:default>
        <div v-if="activeProgramme">
          <section class="incl-section">
            <h4 class="mb-4">Overview</h4>

            <div class="grid grid-cols-2 gap-4 sm:grid-cols-1">
              <div>
                <p class="category">Project length</p>
                <p>{{ tripLength }}</p>
              </div>

              <div>
                <p class="category">Diving</p>
                <p>
                  {{ isDivingIncluded ? 'Included' : 'Not included' }}
                </p>
              </div>

              <div>
                <p class="category">Minimum age</p>
                <p>{{ activeProgramme.minimum_age }}</p>
              </div>

              <div>
                <p class="category">Start date</p>
                <p v-if="typeof startDate === 'string'">
                  {{ startDate }}
                </p>
                <div v-else-if="Array.isArray(startDate)">
                  <ul>
                    <li v-for="(date, index) in startDate" :key="index">
                      {{ date }}
                    </li>
                  </ul>
                </div>
              </div>

              <div class="marine-focus">
                <p class="category">Marine focus</p>
                <p>
                  {{ activeProgramme.marine_focus.map(item => item.name).join(', ') }}
                </p>
              </div>
            </div>

            <hr class="separator" />
          </section>

          <section class="incl-section">
            <h4 class="mb-4">Description</h4>
            <div class="paragraph" v-html="activeProgramme.long_description" />

            <hr class="separator" />
          </section>

          <section v-if="included.length" class="incl-section mb-4">
            <h4 class="mb-4">Inclusions</h4>

            <h3 class="mb-4">Included</h3>
            <div class="grid grid-cols-2 gap-4 sm:grid-cols-1">
              <div v-for="incl in included" :key="incl.id" class="flex items-center">
                <img :src="incl.icon_path" class="inline-block mr-4" width="32" height="32" />
                {{ incl.name }}
              </div>
            </div>

            <template v-if="notIncluded.length">
              <h3 class="mb-4 mt-8">Not included</h3>
              <div class="grid grid-cols-2 gap-4 sm:grid-cols-1">
                <div v-for="incl in notIncluded" :key="incl.id" class="flex items-center">
                  <img :src="incl.icon_path" class="inline-block mr-4" width="32" height="32" />
                  <span>{{ incl.name }}</span>
                </div>
              </div>
            </template>
          </section>
        </div>
      </template>

      <template #stickyFooter="{ modal }">
        <footer class="text-right zb-modal-footer" @click="modal.hide">
          <button type="button" class="btn btn-secondary">Close</button>
        </footer>
      </template>
    </zb-modal>
  </div>
</template>

<script>
import orderBy from 'lodash/orderBy'
import ZbModal from '../core/ZbModal'
import { mapState } from 'vuex'
import {
  ECOVENTURE_BASIC_INCLUSIONS,
  ECOVENTURE_CATEGORIES
} from '../../helpers/constants'
import {
  getTripLength
} from '../../helpers/ecoventureHelpers'

const today = moment()

export default {
  components: {
    ZbModal
  },
  computed: {
    ...mapState(['activeProgramme', 'programmes']),
    tripLength () {
      return getTripLength(this.activeProgramme)
    },
    isDivingIncluded () {
      const divingInclusion = ECOVENTURE_BASIC_INCLUSIONS.find(incl => incl.is_diving === true)

      return this.activeProgramme.inclusions.some(incl => incl.id === divingInclusion.id)
    },
    startDate () {
      if (this.activeProgramme.category === ECOVENTURE_CATEGORIES.ECOVENTURE) {
        return this.activeProgramme.start_date_ecoventure
      }
      else if (this.activeProgramme.category === ECOVENTURE_CATEGORIES.EXPEDITION) {
        return this.activeProgramme.start_date_expedition
          .map(date => moment.utc(date))
          .filter(mmDate => mmDate.isValid())
          .filter(mmDate => mmDate.isAfter(today, 'day'))
          .sort((dateA, dateB) => {
            return dateA.format('YYYYMMDD') - dateB.format('YYYYMMDD')
          })
          .map(mmDate => mmDate.format('DD MMM YYYY'))
      }

      return ''
    },
    basicInclusions () {
      const ids = ECOVENTURE_BASIC_INCLUSIONS
        .filter(incl => incl.is_essential)
        .map(incl => incl.id)

      return orderBy(this.activeProgramme.inclusions.filter(incl => {
        return ids.includes(incl.id)
      }), ['order'], ['asc'])
      .map(incl => {
        incl.is_basic_inclusion = true

        return incl
      })
    },
    otherInclusions () {
      const ids = ECOVENTURE_BASIC_INCLUSIONS.map(incl => incl.id)

      return orderBy(this.activeProgramme.inclusions.filter(incl => {
        return ids.includes(incl.id) === false
      }), ['order'], ['asc'])
      .map(incl => {
        incl.is_basic_inclusion = false

        return incl
      })
    },
    included () {
      return this.basicInclusions.concat(this.otherInclusions)
    },
    notIncluded () {
      const result = []
      const essentials = ECOVENTURE_BASIC_INCLUSIONS.filter(incl => incl.is_essential)

      for (const incl of essentials) {
        if (!this.activeProgramme.inclusions.find(_incl => _incl.id === incl.id)) {
          result.push(incl.id)
        }
      }

      return result.map(id => {
        return essentials.find(incl => incl.id === id)
      })
    }
  },
  methods: {
    handleModalClose () {
      setTimeout(() => {
        this.$store.commit('setActiveProgramme', null)
      }, 500)
    }
  },
  watch: {
    activeProgramme (activeProgramme) {
      if (activeProgramme) {
        this.$refs.detailModal.show()
      }
      else {
        this.$refs.detailModal.hide()
      }
    }
  }
}
</script>

<style scoped>
.category {
  color: var(--black-light-5);
}

.separator {
  margin-bottom: 32px;
  margin-top: 32px;
}

@media (min-width: 601px) {
  .marine-focus {
    grid-column-start: 1;
    grid-column-end: 3;
  }
}
</style>